import { ProductModelType, type_code_bar } from '@teinor/erp/types/company/product'
import { useCallback, useMemo } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import ProductLayout from '../..'
import { ComponentWithPermissions } from '../../../../../baseComponents/CreateRoutering/types'
import FormRenderer from '../../../../../forms/FormRenderer'
import { FormRender } from '../../../../../forms/FormRenderer/types'
import TwinForm from '../../../../../forms/TwinForm'
import { BeforeSubmitHandler } from '../../../../../forms/TwinForm/types'
import { addDictionaryExtraLine, dictionaryComplexFromJsonArr, dictionaryComplexFromArr, addDictionaryExtraLineInSectionItems } from '../../../../../utils/globals/dictionary'
import { valueOrDefaultValue } from '../../../../../utils/globals/valueOrDefaultValue'
import withLoading from '../../../../../utils/hoc/withLoading'
import useIsLoading from '../../../../../utils/hooks/useIsLoading'
import useMeasureDictionary from '../../../../../utils/hooks/useMeasureDictionary'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../utils/reducers'
import { VariantsPayload } from '../../../../../utils/reducers/products/variants'
import { changeErrorMessage } from '../../../../../utils/reducers/reduxDispatch'
import { ProductExtendedTyShort } from '../../../types'
import { getISOCurrencyMain } from '../../../../../utils/reducers/getters'
import { ProductTypesPayload } from '../../../../../utils/reducers/products/productTypes'
import useWasteWeightProduct from '../../../../../utils/hooks/product/useWasteWeightProduct'
import useProductAddonTypeDict from '../../../../../utils/hooks/orders/useProductAddonTypeDict'

type OtherInformationProps = ComponentWithPermissions & UseOtherInformationLogicProps & {
    basicData: ProductExtendedTyShort
    getData: () => Promise<void>
}

const OtherInformation: React.FC<OtherInformationProps> = ({ basicData, getData, userPermissions,  ...logic }) => {
    const { t } = useTwinTranslation()
    const unitTypes = {
        0: { name: t('units', 'Unidades') },
        1: { name: t('measureU', 'U. Medida') }
    }
    const controlUnitTypes = {
        0: { name: t('units', 'Unidades') },
        1: { name: t('measureU', 'U. Medida') },
        2: { name: t('unitsAndMeasureU', 'Unidades y U. Medida') }
    }
    const onOff = {
        'on': { name: t('on', 'Activado') },
        'off': { name: t('disabledS', 'Desactivado') },
    }
    const { dictTypeCodeBar, beforeSubmitHandler, endLoading, loading, variantsParsed } = useOtherInformationLogic({ ...logic })
    const { measureItems } = useMeasureDictionary()
    const dictProductTypes = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(logic.productTypes || {}))), [logic.productTypes])
    const { dictWasteWeightType, weightInfo, handleChangeWeightInfo } = useWasteWeightProduct({basicData})
    const productAddonTypeDict = useProductAddonTypeDict()
    const memoProductAddonTypeDict = useMemo(() => addDictionaryExtraLine(productAddonTypeDict), [productAddonTypeDict])
    const fields: FormRender<ProductModelType> = [
        {
            cols: 2,
            title: t('references', 'Referencias'),
            elements: [
                {
                    label: t('typeCodeBar', 'Tipo de código de barras'),
                    name: 'type_code_bar',
                    component: 'CustomSelect',
                    items: dictTypeCodeBar,
                    value: basicData.type_code_bar as any
                },
                {
                    label: t('codeBar', 'Código de barras'),
                    name: 'code_bar',
                    component: 'InputWithLabelMargin',
                },
                {
                    label: t('customCodeBar', 'Código de barras personalizado'),
                    name: 'custom_code_bar',
                    component: 'InputWithLabelMargin',
                },
                {
                    label: t('secondaryReference', 'Referencia secundaria'),
                    name: 'secondaryReference',
                    component: 'InputWithLabelMargin',
                },
                {
                    name: 'id',
                    component: 'InputHidden',
                },
            ]
        },
        {
            cols: 2,
            title: t('dimensionsAndWeight', 'Dimensión y peso'),
            elements: [
                {
                    label: t('dimensions', 'Dimensiones'),
                    name: 'dimensions',
                    component: 'InputWithLabelMargin',
                },
                {
                    label: t('weight', 'Peso'),
                    name: 'weight',
                    component: 'InputNumberWithLabelMargin',
                    value: weightInfo?.weight || '',
                    onChange: (val) => handleChangeWeightInfo('weight', val),
                },
                {
                    label: t('unitTypePurchase', 'Tipo de Unidades de Compra'),
                    name: 'unitTypePurchase',
                    component: 'CustomSelect',
                    items: unitTypes,
                },
                {
                    label: t('unitTypeSelling', 'Tipo de Unidades de Venta'),
                    name: 'unitTypeSelling',
                    component: 'CustomSelect',
                    items: unitTypes,
                },
                {
                    label: t('controlUnitType', 'Control de Unidades de Inventario'),
                    name: 'controlUnitType',
                    component: 'CustomSelect',
                    items: controlUnitTypes,
                },
                {
                    name: 'unitMeasure',
                    label: t('unitMeasure', 'Unidad de Medida'),
                    component: 'CustomSelectSectionsSearchbar',
                    items: addDictionaryExtraLineInSectionItems(measureItems),
                },
                {
                    label: t('wasteWeightType', 'Tipo de merma'),
                    name: 'wasteWeightType',
                    component: 'CustomSelectWithSearchBar',
                    items: dictWasteWeightType,
                    value: String(weightInfo?.wasteWeightType || 0),
                    onChange: (val) => handleChangeWeightInfo('wasteWeightType', val),
                },
                {
                    label: t('wasteWeight', 'Merma'),
                    name: 'wasteWeight',
                    component: 'InputNumberWithLabelMargin',
                    value: weightInfo?.wasteWeight || '',
                    onChange: (val) => handleChangeWeightInfo('wasteWeight', val),
                },
                {
                    label: t('calculatedWeight', 'Peso calculado'),
                    name: 'calculatedWeight',
                    component: 'InputNumberWithLabelMargin',
                    disabled: true,
                    value: weightInfo?.calculatedWeight || '',
                },
            ]
        },
        {
            cols: 2,
            title: t('others', 'Otros'),
            elements: [
                {
                    name: 'VariantGroupMainId',
                    label: t('variantGroupMain', 'Variante principal'),
                    component: 'CustomSelect',
                    items: addDictionaryExtraLine(variantsParsed),
                    sortItems: true
                },
                {
                    name: 'subProductPricesConfig',
                    label: t('subProductPricesParam', 'Precios de los subproductos'),
                    component: 'CustomSelect',
                    items: {
                        'heredate': { name: t('fromParent', 'Del padre') },
                        'self': { name: t('selfPrices', 'Precios propios') },
                        'increment': { name: t('incrementFromParent', 'Incrementar sobre el padre') }
                    }
                }, 
                {
                    name: 'operateStock',
                    label: t('stockControl', 'Controlar stock'),
                    component: 'CustomSelect',
                    items: addDictionaryExtraLine(onOff),
                    sortItems: true
                },
                {
                    label: t('margin', 'Margen'),
                    name: 'margin',
                    component: 'InputNumberWithLabelMarginStateFull',
                },
                {
                    label: t('costPrice', 'Precio de coste'),
                    name: 'costPrice',
                    component: 'InputPriceStateFull',
                    CurrencyId: getISOCurrencyMain()
                },
                {
                    label: t('calculatedCost', 'Coste calculado'),
                    name: 'calculatedCost',
                    component: 'InputPriceStateFull',
                    readOnly: true,
                    CurrencyId: getISOCurrencyMain()
                },
                {
                    label: t('productTypeSelector', 'Tipo de producto'),
                    name: 'ProductTypeId',
                    component: 'CustomSelectWithSearchBar',
                    items: dictProductTypes,
                    sortItems: true
                },
                {
                    name: 'featuredColor',
                    label: t('featuredColor', 'Color destacado'),
                    component: 'InputColorDeleteButtonWithMargin',
                    defaultValue: basicData?.featuredColor
                },
                {
                    name: 'productAddonType',
                    label: t('productAddonType', 'Tipo de complementos'),
                    component: 'CustomSelect',
                    items: memoProductAddonTypeDict,
                    sortItems: true, 
                },
            ]
        },
        {
            cols: 1,
            title: t('notes', 'Notas'),
            elements: [
                {
                    name: 'notes',
                    component: 'TwinEditor',
                    className: 'h-200',
                    defaultValue: basicData?.['notes'] ?? '',
                },
            ]
        }
    ]
    const parsedFields = valueOrDefaultValue(fields, userPermissions, basicData)
    return (
        <TwinForm className='flex flex-auto' action='/api/app/product/updateProduct' onSubmit={() => { endLoading(); getData() }} beforeSubmitHandler={beforeSubmitHandler} onError={endLoading}>
            <ProductLayout basicData={basicData} userPermissions={userPermissions} buttonIsDisabled={loading}>
                <FormRenderer sections={parsedFields} antiUnsaveTrigger={true} className='product_container h-1'/>
            </ProductLayout>
        </TwinForm>
    )
}

interface UseOtherInformationLogicProps extends ReduxVariants { }

const useOtherInformationLogic = ({ variants }: UseOtherInformationLogicProps) => {
    const { startLoading, endLoading, loading } = useIsLoading()
    const variantsParsed = dictionaryComplexFromJsonArr(variants?.variants || {})
    const dictTypeCodeBar = dictionaryComplexFromArr(type_code_bar as any)
    const { t } = useTwinTranslation()
    const mesureRequiredText = t('unitMeasureRequiredForConfiguration', 'La unidad de medida es necesaria para la configuración puesta')
    const beforeSubmitHandler: BeforeSubmitHandler = useCallback((vals) => {
        const someConfigInWeight = parseInt(vals['unitTypePurchase']) !== 0 || parseInt(vals['unitTypeSelling']) !== 0 || parseInt(vals['controlUnitType']) !== 0
        const unitDefined = vals['unitMeasure']
        if (someConfigInWeight && !unitDefined) {
            changeErrorMessage(mesureRequiredText)
            return false
        }
        startLoading()
        return true
    }, [startLoading, mesureRequiredText])

    return { startLoading, endLoading, loading, beforeSubmitHandler, variantsParsed, dictTypeCodeBar }
}

const VariantsDispatch = {
    setVariants: (payload: VariantsPayload) => ({ type: 'CHANGE_VARIANTS', payload }),
    setProductTypes: (payload: ProductTypesPayload) => ({ type: 'CHANGE_PRODUCT_TYPES', payload }),
}

export type ReduxVariants = ConnectedProps<typeof VariantsConnect>
const mapVariantsConnector = (state: AllReduxPayloads) => ({ variants: state.variants, productTypes: state.productTypes })
const VariantsConnect = connect(mapVariantsConnector, VariantsDispatch)

const VariantsConnectLoading = withLoading(OtherInformation, [{ fetchUrl: '/api/app/product/productVariant/getAllVariantsComplete', propName: 'variants', setFunctionName: 'setVariants' }, { fetchUrl: '/api/app/product/productType/getAllProductTypes', propName: 'productTypes', setFunctionName: 'setProductTypes' }])

const OtherInformationConnect = VariantsConnect(VariantsConnectLoading)

export default OtherInformationConnect